import { useState } from 'react'
import fs from 'fs'
import path from 'path'
import Link from 'next/link'
import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import FinalCta from '@comp/home/final-cta'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'
import { Localized } from '@lib/glossary/types'

const finds: { title: Localized; body: Localized }[] = [
  {
    title: {
      en: 'Kerberoastable accounts',
      tr: 'Kerberoast edilebilir hesaplar',
    },
    body: {
      en: 'Every account with a ServicePrincipalName is an offline password-cracking target. The script surfaces all of them.',
      tr: 'ServicePrincipalName tanımlı her hesap, çevrimdışı parola kırma hedefidir. Script hepsini ortaya çıkarır.',
    },
  },
  {
    title: {
      en: 'Passwords that never expire',
      tr: 'Süresi hiç dolmayan parolalar',
    },
    body: {
      en: 'PasswordNeverExpires on a service account usually means the password is years old, and shared in a wiki somewhere.',
      tr: 'Bir servis hesabında PasswordNeverExpires genellikle parolanın yıllar önce belirlendiği ve bir yerlerde wiki’de paylaşıldığı anlamına gelir.',
    },
  },
  {
    title: { en: 'Stale credentials', tr: 'Bayat kimlik bilgileri' },
    body: {
      en: 'Accounts whose password predates your retention policy, flagged with configurable thresholds.',
      tr: 'Parolası saklama politikanızdan daha eski olan hesaplar, yapılandırılabilir eşiklerle işaretlenir.',
    },
  },
  {
    title: {
      en: 'Privileged service accounts',
      tr: 'Ayrıcalıklı servis hesapları',
    },
    body: {
      en: 'Service accounts sitting in Domain Admins or other privileged groups: the finding auditors write up first.',
      tr: 'Domain Admins veya diğer ayrıcalıklı gruplarda duran servis hesapları: denetçilerin rapora ilk yazdığı bulgu.',
    },
  },
  {
    title: { en: 'Dormant but enabled', tr: 'Atıl ama etkin' },
    body: {
      en: 'Enabled accounts with no recent logon: forgotten integrations that still authenticate if someone finds the password.',
      tr: 'Yakın zamanda oturum açmamış etkin hesaplar: parolayı bulan biri için hâlâ kimlik doğrulayan unutulmuş entegrasyonlar.',
    },
  },
  {
    title: { en: 'Dangerous delegation', tr: 'Tehlikeli delegasyon' },
    body: {
      en: 'Unconstrained and constrained delegation settings that turn one compromised account into many.',
      tr: 'Ele geçirilen tek bir hesabı birçok hesaba dönüştüren kısıtsız ve kısıtlı delegasyon ayarları.',
    },
  },
]

const steps: { title: Localized; body: Localized }[] = [
  {
    title: { en: 'Review the script', tr: 'Script’i inceleyin' },
    body: {
      en: 'It is ~150 lines of read-only Get-AD* queries. Read it; you should never run an unreviewed script against your domain.',
      tr: 'Yaklaşık 150 satırlık, salt okunur Get-AD* sorgularından oluşur. Okuyun; domaininize karşı incelenmemiş bir script asla çalıştırmamalısınız.',
    },
  },
  {
    title: {
      en: 'Run it from any domain-joined machine',
      tr: 'Domaine bağlı herhangi bir makineden çalıştırın',
    },
    body: {
      en: 'Requires the RSAT ActiveDirectory module and ordinary read access. No elevated rights, no changes to AD, no network calls.',
      tr: 'RSAT ActiveDirectory modülü ve sıradan okuma erişimi yeterli. Yükseltilmiş yetki yok, AD’de değişiklik yok, ağ çağrısı yok.',
    },
  },
  {
    title: { en: 'Triage the CSV', tr: 'CSV’yi önceliklendirin' },
    body: {
      en: 'Results are sorted by risk-flag count. Start at the top: privileged accounts with stale, never-expiring passwords.',
      tr: 'Sonuçlar risk bayrağı sayısına göre sıralanır. En üstten başlayın: bayat ve süresi dolmayan parolalara sahip ayrıcalıklı hesaplar.',
    },
  },
]

const faqs: { q: Localized; a: Localized }[] = [
  {
    q: {
      en: 'Is the script safe to run in production?',
      tr: 'Script’i üretim ortamında çalıştırmak güvenli mi?',
    },
    a: {
      en: 'It is read-only by construction: only Get-AD* cmdlets, no Set-, New-, or Remove- calls, and no network egress. Output is a local CSV. Still, review it first and run it under a normal (non-admin) account with read access, as you would any third-party script.',
      tr: 'Yapısı gereği salt okunurdur: yalnızca Get-AD* cmdlet’leri; Set-, New- veya Remove- çağrısı ve dışarıya ağ trafiği yoktur. Çıktı yerel bir CSV’dir. Yine de her üçüncü taraf script’te olduğu gibi önce inceleyin ve okuma erişimi olan normal (yönetici olmayan) bir hesapla çalıştırın.',
    },
  },
  {
    q: {
      en: 'What do I do with the results?',
      tr: 'Sonuçlarla ne yapmalıyım?',
    },
    a: {
      en: 'Vault the flagged credentials, rotate the stale ones, remove service accounts from privileged groups where possible, and put the rest behind time-bound, recorded access. That is exactly the workflow Monopam automates, but the inventory is valuable regardless of what PAM you use.',
      tr: 'İşaretlenen kimlik bilgilerini kasaya alın, bayat olanları rotasyona sokun, mümkün olan yerlerde servis hesaplarını ayrıcalıklı gruplardan çıkarın ve kalanları süre sınırlı, kayıt altındaki erişimin arkasına koyun. Monopam’ın otomatikleştirdiği iş akışı tam olarak budur; ama hangi PAM’i kullanırsanız kullanın bu envanter değerlidir.',
    },
  },
  {
    q: {
      en: 'Why do service accounts matter so much?',
      tr: 'Servis hesapları neden bu kadar önemli?',
    },
    a: {
      en: 'They hold standing privilege, their passwords rarely rotate, and nobody owns them. Post-incident reports repeatedly find service accounts as the lateral-movement path, and both KVKK and DORA audits ask for exactly this inventory.',
      tr: 'Kalıcı ayrıcalık taşırlar, parolaları nadiren döner ve kimse onları sahiplenmez. Olay sonrası raporlar, yanal hareket yolu olarak tekrar tekrar servis hesaplarını gösterir; hem KVKK hem DORA denetimleri tam olarak bu envanteri ister.',
    },
  },
  {
    q: {
      en: 'Does anything get sent to Monofor?',
      tr: 'Monofor’a herhangi bir veri gönderiliyor mu?',
    },
    a: {
      en: 'No. The script has no telemetry and makes no network calls; results stay in the CSV on your machine. The download is a plain .ps1 you can diff against the code shown on this page.',
      tr: 'Hayır. Script’te telemetri yoktur ve hiçbir ağ çağrısı yapmaz; sonuçlar makinenizdeki CSV’de kalır. İndirilen dosya, bu sayfada gösterilen kodla karşılaştırabileceğiniz düz bir .ps1’dir.',
    },
  },
]

const ui = {
  seoTitle: {
    en: 'Free AD Service Account Scanner (PowerShell) | Monofor',
    tr: 'Ücretsiz AD Servis Hesabı Tarayıcı (PowerShell) | Monofor',
  },
  seoDescription: {
    en: 'Free read-only PowerShell script: inventory Active Directory service accounts and flag kerberoastable SPNs, stale passwords, privileged membership, and risky delegation.',
    tr: 'Ücretsiz, salt okunur PowerShell script’i: Active Directory servis hesaplarını envantere alın; kerberoast edilebilir SPN’leri, bayat parolaları, ayrıcalıklı grup üyeliklerini ve riskli delegasyonu işaretleyin.',
  },
  jsonLdDescription: {
    en: 'Read-only PowerShell script that inventories Active Directory service accounts and flags privileged-access risks.',
    tr: 'Active Directory servis hesaplarını envantere alan ve ayrıcalıklı erişim risklerini işaretleyen salt okunur PowerShell script’i.',
  },
  eyebrow: { en: 'Free tool · PowerShell', tr: 'Ücretsiz araç · PowerShell' },
  title: {
    en: 'Find the service accounts nobody owns.',
    tr: 'Kimsenin sahiplenmediği servis hesaplarını bulun.',
  },
  lede: {
    en: 'A read-only PowerShell script that inventories your Active Directory service accounts and flags the risks auditors (and attackers) look for first. No agent, no telemetry, results stay on your machine.',
    tr: 'Active Directory servis hesaplarınızı envantere alan ve denetçilerin (ve saldırganların) ilk baktığı riskleri işaretleyen salt okunur bir PowerShell script’i. Ajan yok, telemetri yok; sonuçlar makinenizde kalır.',
  },
  downloadCta: {
    en: 'Download Find-ServiceAccounts.ps1',
    tr: 'Find-ServiceAccounts.ps1’i indir',
  },
  readSourceCta: { en: 'Read the source below', tr: 'Kaynağı aşağıda okuyun' },
  heroMeta: {
    en: 'MIT licensed · read-only Get-AD* queries · ~150 lines',
    tr: 'MIT lisanslı · salt okunur Get-AD* sorguları · ~150 satır',
  },
  findsEyebrow: { en: 'What it flags', tr: 'Neleri işaretler' },
  findsHeading: { en: 'Six findings, one CSV.', tr: 'Altı bulgu, tek CSV.' },
  stepsEyebrow: { en: 'How to run it', tr: 'Nasıl çalıştırılır' },
  sourceEyebrow: { en: 'The source', tr: 'Kaynak' },
  copyCta: { en: 'Copy script', tr: 'Script’i kopyala' },
  copiedLabel: { en: 'Copied ✓', tr: 'Kopyalandı ✓' },
  downloadShort: { en: 'Download .ps1', tr: '.ps1 indir' },
  licenseNote: {
    en: 'Provided as-is under the MIT license. Review before running, as you should with any script that touches your directory.',
    tr: 'MIT lisansı altında olduğu gibi sunulur. Çalıştırmadan önce inceleyin, dizininize dokunan her script’te yapmanız gerektiği gibi.',
  },
  vaultHeading: {
    en: 'Found something? Now vault it.',
    tr: 'Bir şey mi buldunuz? Şimdi kasaya alın.',
  },
  vaultBody1: {
    en: 'The scan gives you the inventory; the fix is discipline around those credentials: vault them, rotate them, and put access behind approvals and recording. ',
    tr: 'Tarama size envanteri verir; çözüm, bu kimlik bilgileri etrafındaki disiplindir: kasaya alın, rotasyona sokun ve erişimi onay ile kayıt arkasına koyun. ',
  },
  vaultBody2: {
    en: ' automates exactly that, and imports this CSV as a starting point. New to the topic? Start with ',
    tr: ' tam olarak bunu otomatikleştirir ve bu CSV’yi başlangıç noktası olarak içe aktarır. Konuya yeni misiniz? ',
  },
  vaultGlossary1: {
    en: 'what a service account is',
    tr: 'servis hesabı nedir',
  },
  vaultAnd: { en: ' and ', tr: ' ve ' },
  vaultGlossary2: { en: 'credential vaulting', tr: 'kimlik bilgisi kasalama' },
  vaultBodyEnd: { en: '.', tr: ' ile başlayın.' },
  faqEyebrow: { en: 'FAQ', tr: 'SSS' },
  faqHeading: { en: 'Before you run it.', tr: 'Çalıştırmadan önce.' },
}

function pick(v: Localized, locale: string) {
  return locale === 'tr' ? v.tr || v.en : v.en
}

type Props = { scriptSource: string; locale: string }

export default function AdServiceAccountScannerPage({
  scriptSource,
  locale,
}: Props) {
  const [copied, setCopied] = useState(false)
  const basePath = locale === 'tr' ? '/tr' : ''
  const p = (v: Localized) => pick(v, locale)

  const copyScript = async () => {
    try {
      await navigator.clipboard.writeText(scriptSource)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard unavailable; the download button still works
    }
  }

  return (
    <Layout>
      <Seo
        title={p(ui.seoTitle)}
        description={p(ui.seoDescription)}
        path={`${basePath}/tools/ad-service-account-scanner`}
      />
      <Head>
        <link
          rel="alternate"
          hrefLang="en"
          href={`${SITE_URL}/tools/ad-service-account-scanner`}
        />
        <link
          rel="alternate"
          hrefLang="tr"
          href={`${SITE_URL}/tr/tools/ad-service-account-scanner`}
        />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${SITE_URL}/tools/ad-service-account-scanner`}
        />
      </Head>
      <JsonLd
        id="script-tool"
        data={{
          '@type': 'SoftwareSourceCode',
          name: 'Find-ServiceAccounts.ps1',
          description: p(ui.jsonLdDescription),
          programmingLanguage: 'PowerShell',
          codeRepository: `${SITE_URL}${basePath}/tools/ad-service-account-scanner`,
          license: 'https://opensource.org/licenses/MIT',
          author: { '@type': 'Organization', name: 'Monofor' },
        }}
      />
      <JsonLd
        id="script-faq"
        data={{
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: p(f.q),
            acceptedAnswer: { '@type': 'Answer', text: p(f.a) },
          })),
        }}
      />
      <main className="bg-surface text-ink-primary">
        {/* Hero */}
        <Section tone="default" density="compact" className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
            <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
          </div>
          <Container size="xl" className="relative">
            <div className="max-w-3xl">
              <Eyebrow tone="brand">{p(ui.eyebrow)}</Eyebrow>
              <div className="mt-6">
                <Heading as="h1" size="display-xl">
                  {p(ui.title)}
                </Heading>
              </div>
              <div className="mt-8 max-w-2xl">
                <Text size="xl" tone="secondary">
                  {p(ui.lede)}
                </Text>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Button
                  href="/downloads/Find-ServiceAccounts.ps1"
                  variant="primary"
                  size="lg"
                >
                  {p(ui.downloadCta)}
                </Button>
                <Button href="#script" variant="outline" size="lg">
                  {p(ui.readSourceCta)}
                </Button>
              </div>
              <div className="mt-6 text-body-sm text-ink-tertiary">
                {p(ui.heroMeta)}
              </div>
            </div>
          </Container>
        </Section>

        {/* What it finds */}
        <Section tone="subtle" density="default">
          <Container size="xl">
            <div className="max-w-3xl">
              <Eyebrow tone="accent">{p(ui.findsEyebrow)}</Eyebrow>
              <Heading as="h2" size="display-lg" className="mt-5">
                {p(ui.findsHeading)}
              </Heading>
            </div>
            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {finds.map((f) => (
                <div
                  key={f.title.en}
                  className="rounded-2xl border border-line-subtle bg-surface p-6 shadow-card"
                >
                  <div className="text-body-md font-medium text-ink-primary">
                    {p(f.title)}
                  </div>
                  <Text size="sm" tone="tertiary" className="mt-2">
                    {p(f.body)}
                  </Text>
                </div>
              ))}
            </div>
          </Container>
        </Section>

        {/* How to run */}
        <Section tone="default" density="compact">
          <Container size="md">
            <Eyebrow tone="brand">{p(ui.stepsEyebrow)}</Eyebrow>
            <ol className="mt-8 space-y-5">
              {steps.map((step, i) => (
                <li
                  key={step.title.en}
                  className="grid gap-4 rounded-2xl border border-line-subtle bg-surface-subtle p-6 lg:grid-cols-[56px_1fr]"
                >
                  <div className="text-display-sm font-medium text-brand-900/40 tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <div>
                    <div className="text-body-lg font-medium text-ink-primary">
                      {p(step.title)}
                    </div>
                    <Text size="md" tone="secondary" className="mt-2">
                      {p(step.body)}
                    </Text>
                  </div>
                </li>
              ))}
            </ol>
          </Container>
        </Section>

        {/* Source */}
        <Section tone="inverse" density="default" id="script">
          <Container size="lg">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <Eyebrow tone="accent">{p(ui.sourceEyebrow)}</Eyebrow>
                <Heading as="h2" size="display-md" tone="inverse" className="mt-3">
                  Find-ServiceAccounts.ps1
                </Heading>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={copyScript}
                  className="rounded-pill border border-white/30 px-5 py-2.5 text-body-sm font-medium text-white transition hover:bg-white/10"
                >
                  {copied ? p(ui.copiedLabel) : p(ui.copyCta)}
                </button>
                <a
                  href="/downloads/Find-ServiceAccounts.ps1"
                  className="rounded-pill bg-white px-5 py-2.5 text-body-sm font-medium text-brand-900 transition hover:bg-brand-50"
                >
                  {p(ui.downloadShort)}
                </a>
              </div>
            </div>
            <pre className="mt-8 max-h-[560px] overflow-auto rounded-2xl border border-white/10 bg-black/30 p-6 text-[13px] leading-relaxed text-brand-100">
              <code>{scriptSource}</code>
            </pre>
            <div className="mt-4 text-body-sm text-brand-200/90">
              {p(ui.licenseNote)}
            </div>
          </Container>
        </Section>

        {/* After the scan */}
        <Section tone="subtle" density="compact">
          <Container size="md">
            <Heading as="h2" size="display-md">
              {p(ui.vaultHeading)}
            </Heading>
            <Text size="lg" tone="secondary" className="mt-5">
              {p(ui.vaultBody1)}
              <Link
                href="/pam"
                className="font-medium text-brand-700 underline-offset-4 hover:underline"
              >
                Monopam
              </Link>
              {p(ui.vaultBody2)}
              <Link
                href="/glossary/service-account"
                className="font-medium text-brand-700 underline-offset-4 hover:underline"
              >
                {p(ui.vaultGlossary1)}
              </Link>
              {p(ui.vaultAnd)}
              <Link
                href="/glossary/credential-vaulting"
                className="font-medium text-brand-700 underline-offset-4 hover:underline"
              >
                {p(ui.vaultGlossary2)}
              </Link>
              {p(ui.vaultBodyEnd)}
            </Text>
          </Container>
        </Section>

        {/* FAQ */}
        <Section tone="default" density="default">
          <Container size="md">
            <Eyebrow tone="brand">{p(ui.faqEyebrow)}</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              {p(ui.faqHeading)}
            </Heading>
            <dl className="mt-12 space-y-4">
              {faqs.map((faq) => (
                <div
                  key={faq.q.en}
                  className="rounded-2xl border border-line-subtle bg-surface-subtle p-6 lg:p-8"
                >
                  <dt className="text-body-lg font-medium text-ink-primary">
                    {p(faq.q)}
                  </dt>
                  <dd className="mt-3 text-body-md text-ink-secondary">
                    {p(faq.a)}
                  </dd>
                </div>
              ))}
            </dl>
          </Container>
        </Section>

        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale?: string }) {
  const scriptSource = fs.readFileSync(
    path.join(process.cwd(), 'public', 'downloads', 'Find-ServiceAccounts.ps1'),
    'utf8'
  )
  return { props: { scriptSource, locale: locale ?? 'en' } }
}
